import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { DemoStorageService } from '../../core/demo-storage.service';
import type { VersionPolitica, CompromisoRecurso, ResponsableSST, EstadoPolitica } from './models/politica-liderazgo.model';

const KEY_VERSIONES = 'politica_versiones';
const KEY_COMPROMISOS = 'politica_compromisos';
const KEY_RESPONSABLES = 'politica_responsables';

function demoVersiones(): VersionPolitica[] {
  const now = new Date().toISOString().slice(0, 19).replace('T', ' ');
  const d = (daysAgo: number) =>
    new Date(Date.now() - daysAgo * 24 * 3600 * 1000).toISOString().slice(0, 10);
  return [
    {
      id: '1',
      version: 2,
      fechaAprobacion: d(180),
      aprobadoPor: 'Gerencia General',
      contenido: 'La empresa se compromete con la prevención de lesiones y enfermedades...',
      estado: 'vigente',
      createdAt: now,
      updatedAt: now,
    },
    {
      id: '2',
      version: 1,
      fechaAprobacion: d(400),
      aprobadoPor: 'Gerencia General',
      contenido: 'Versión inicial de la política SST.',
      estado: 'obsoleta',
      createdAt: now,
      updatedAt: now,
    },
  ];
}

function demoCompromisos(): CompromisoRecurso[] {
  const now = new Date().toISOString().slice(0, 19).replace('T', ' ');
  const d = (daysAgo: number) =>
    new Date(Date.now() - daysAgo * 24 * 3600 * 1000).toISOString().slice(0, 10);
  return [
    {
      id: '1',
      titulo: 'Recursos para capacitación SST',
      descripcion: 'Asignación presupuestal anual para capacitaciones obligatorias.',
      responsable: 'Gerencia General',
      fechaCompromiso: d(365),
      estado: 'cumplido',
      createdAt: now,
      updatedAt: now,
    },
  ];
}

function demoResponsables(): ResponsableSST[] {
  const now = new Date().toISOString().slice(0, 19).replace('T', ' ');
  const d = (daysAgo: number) =>
    new Date(Date.now() - daysAgo * 24 * 3600 * 1000).toISOString().slice(0, 10);
  return [
    {
      id: '1',
      nombre: 'María García',
      cargo: 'Gerente General',
      rolSST: 'Alta dirección / Responsable final',
      email: 'mgarcia@empresa.com',
      vigenteDesde: d(730),
      createdAt: now,
      updatedAt: now,
    },
    {
      id: '2',
      nombre: 'Carlos López',
      cargo: 'Coordinador SST',
      rolSST: 'Líder técnico SG-SST',
      email: 'clopez@empresa.com',
      vigenteDesde: d(365),
      createdAt: now,
      updatedAt: now,
    },
  ];
}

@Injectable({ providedIn: 'root' })
export class PoliticaLiderazgoService {
  private versiones: VersionPolitica[] = demoVersiones();
  private compromisos: CompromisoRecurso[] = demoCompromisos();
  private responsables: ResponsableSST[] = demoResponsables();

  constructor(private demoStorage: DemoStorageService) {}

  async loadFromDemoStorage(): Promise<void> {
    if (!environment.demo) return;
    const [v, c, r] = await Promise.all([
      this.demoStorage.get<VersionPolitica[]>(KEY_VERSIONES),
      this.demoStorage.get<CompromisoRecurso[]>(KEY_COMPROMISOS),
      this.demoStorage.get<ResponsableSST[]>(KEY_RESPONSABLES),
    ]);
    this.versiones = Array.isArray(v) ? v : demoVersiones();
    this.compromisos = Array.isArray(c) ? c : demoCompromisos();
    this.responsables = Array.isArray(r) ? r : demoResponsables();
    await this.persistDemo();
  }

  private persistDemo(): Promise<void> {
    if (!environment.demo) return Promise.resolve();
    return Promise.all([
      this.demoStorage.set(KEY_VERSIONES, this.versiones),
      this.demoStorage.set(KEY_COMPROMISOS, this.compromisos),
      this.demoStorage.set(KEY_RESPONSABLES, this.responsables),
    ]).then(() => {}).catch(() => {});
  }

  getVersiones(): VersionPolitica[] {
    return [...this.versiones].sort((a, b) => b.version - a.version);
  }

  getCompromisos(): CompromisoRecurso[] {
    return [...this.compromisos];
  }

  getResponsables(): ResponsableSST[] {
    return this.responsables.filter((r) => !r.vigenteHasta || r.vigenteHasta >= new Date().toISOString().slice(0, 10));
  }

  getPoliticaVigente(): VersionPolitica | null {
    return this.versiones.find((v) => v.estado === 'vigente') ?? null;
  }

  getVersionById(id: string): VersionPolitica | null {
    return this.versiones.find((v) => v.id === id) ?? null;
  }

  /** Crea una nueva versión en borrador (o primera versión). */
  createVersion(contenido: string): VersionPolitica {
    const now = new Date().toISOString().slice(0, 19).replace('T', ' ');
    const nextVersion = this.versiones.length === 0 ? 1 : Math.max(...this.versiones.map((v) => v.version)) + 1;
    const nueva: VersionPolitica = {
      id: 'v' + Date.now(),
      version: nextVersion,
      fechaAprobacion: '',
      aprobadoPor: '',
      contenido: contenido.trim(),
      estado: 'borrador',
      createdAt: now,
      updatedAt: now,
    };
    this.versiones.push(nueva);
    this.persistDemo();
    return { ...nueva };
  }

  /** Actualiza contenido y/o estado de una versión. */
  updateVersion(id: string, data: { contenido?: string; estado?: EstadoPolitica }): VersionPolitica | null {
    const v = this.versiones.find((x) => x.id === id);
    if (!v) return null;
    if (data.contenido !== undefined) v.contenido = data.contenido.trim();
    if (data.estado !== undefined) v.estado = data.estado;
    v.updatedAt = new Date().toISOString().slice(0, 19).replace('T', ' ');
    this.persistDemo();
    return { ...v };
  }

  /** Marca esta versión como vigente, firma digital y pasa las demás a obsoleta. */
  firmarVersion(id: string, aprobadoPor: string): VersionPolitica | null {
    const v = this.versiones.find((x) => x.id === id);
    if (!v) return null;
    const fecha = new Date().toISOString().slice(0, 10);
    v.fechaAprobacion = fecha;
    v.aprobadoPor = aprobadoPor;
    v.firmaDigital = `Firmado digitalmente por ${aprobadoPor} el ${fecha}`;
    v.estado = 'vigente';
    v.updatedAt = new Date().toISOString().slice(0, 19).replace('T', ' ');
    this.versiones.filter((x) => x.id !== id).forEach((x) => (x.estado = 'obsoleta'));
    this.persistDemo();
    return { ...v };
  }

  /** Enviar a revisión (borrador → revision). */
  enviarARevision(id: string): VersionPolitica | null {
    return this.updateVersion(id, { estado: 'revision' });
  }

  addCompromiso(data: Omit<CompromisoRecurso, 'id' | 'createdAt' | 'updatedAt'>): CompromisoRecurso {
    const now = new Date().toISOString().slice(0, 19).replace('T', ' ');
    const nueva: CompromisoRecurso = {
      ...data,
      id: 'c' + Date.now(),
      createdAt: now,
      updatedAt: now,
    };
    this.compromisos.push(nueva);
    this.persistDemo();
    return { ...nueva };
  }

  updateCompromiso(id: string, data: Partial<CompromisoRecurso>): CompromisoRecurso | null {
    const c = this.compromisos.find((x) => x.id === id);
    if (!c) return null;
    Object.assign(c, data);
    c.updatedAt = new Date().toISOString().slice(0, 19).replace('T', ' ');
    this.persistDemo();
    return { ...c };
  }

  deleteCompromiso(id: string): boolean {
    const idx = this.compromisos.findIndex((x) => x.id === id);
    if (idx === -1) return false;
    this.compromisos.splice(idx, 1);
    this.persistDemo();
    return true;
  }

  addResponsable(data: Omit<ResponsableSST, 'id' | 'createdAt' | 'updatedAt'>): ResponsableSST {
    const now = new Date().toISOString().slice(0, 19).replace('T', ' ');
    const nuevo: ResponsableSST = {
      ...data,
      id: 'r' + Date.now(),
      createdAt: now,
      updatedAt: now,
    };
    this.responsables.push(nuevo);
    this.persistDemo();
    return { ...nuevo };
  }

  updateResponsable(id: string, data: Partial<ResponsableSST>): ResponsableSST | null {
    const r = this.responsables.find((x) => x.id === id);
    if (!r) return null;
    Object.assign(r, data);
    r.updatedAt = new Date().toISOString().slice(0, 19).replace('T', ' ');
    this.persistDemo();
    return { ...r };
  }
}
